import React, { Component } from "react";
import ReactDOM from "react-dom";
import * as Icon from "react-feather";

/**
 * A typeable single-select combobox for the filter UI. Replaces native
 * <select> so long option lists (e.g. datasource dropdowns with hundreds of
 * rows) and the field picker are searchable.
 *
 * The dropdown is portaled to <body> and positioned from the input's bounding
 * rect so it never gets clipped by the panel's overflow / stacking context.
 *
 * Props:
 *   options     : [{ value, label, group? }]
 *   value       : currently selected value (compared by String())
 *   placeholder : input placeholder when nothing is selected
 *   disabled    : bool
 *   onChange    : (value | null) => void
 */
const MENU_MAX_H = 260;

class FilterCombobox extends Component {
	state = { open: false, query: "", activeIndex: 0, menuStyle: null };

	wrapRef = React.createRef();
	_menuEl = null;
	_activeEl = null;

	componentDidUpdate(_prevProps, prevState) {
		if (this.state.open && this._activeEl && prevState.activeIndex !== this.state.activeIndex) {
			this._activeEl.scrollIntoView({ block: "nearest" });
		}
	}

	componentWillUnmount() {
		this._unbind();
	}

	_bind() {
		document.addEventListener("mousedown", this._onDocMouseDown, true);
		window.addEventListener("scroll", this._position, true);
		window.addEventListener("resize", this._position);
	}

	_unbind() {
		document.removeEventListener("mousedown", this._onDocMouseDown, true);
		window.removeEventListener("scroll", this._position, true);
		window.removeEventListener("resize", this._position);
	}

	_onDocMouseDown = e => {
		const w = this.wrapRef.current;
		if (w && w.contains(e.target)) return;
		if (this._menuEl && this._menuEl.contains(e.target)) return;
		this.close();
	};

	_selectedLabel() {
		const { options, value } = this.props;
		if (value == null || value === "") return "";
		const found = (options || []).find(o => String(o.value) === String(value));
		return found ? found.label : "";
	}

	_filtered() {
		const { options } = this.props;
		const q = this.state.query.trim().toLowerCase();
		const list = options || [];
		if (!q) return list;
		return list.filter(o => (o.label || "").toLowerCase().indexOf(q) !== -1);
	}

	_position = () => {
		const el = this.wrapRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const vh = window.innerHeight;
		const below = vh - rect.bottom;
		const openUp = below < MENU_MAX_H + 8 && rect.top > below;
		const style = { position: "fixed", left: rect.left, width: rect.width, maxHeight: MENU_MAX_H, zIndex: 9999 };
		if (openUp) style.bottom = vh - rect.top + 4;
		else style.top = rect.bottom + 4;
		this.setState({ menuStyle: style });
	};

	open = () => {
		if (this.props.disabled || this.state.open) return;
		this._bind();
		this._position();
		this.setState({ open: true, query: "", activeIndex: 0 });
	};

	close = () => {
		this._unbind();
		this.setState({ open: false, query: "" });
	};

	onSelect = opt => {
		this.props.onChange(opt.value);
		this.close();
	};

	onInputChange = e => {
		if (!this.state.open) {
			this._bind();
			this._position();
		}
		this.setState({ open: true, query: e.target.value, activeIndex: 0 });
	};

	onKeyDown = e => {
		const list = this._filtered();
		if (e.key === "ArrowDown") {
			e.preventDefault();
			this.setState(s => ({ open: true, activeIndex: Math.min(list.length - 1, s.activeIndex + 1) }));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			this.setState(s => ({ activeIndex: Math.max(0, s.activeIndex - 1) }));
		} else if (e.key === "Enter") {
			if (this.state.open && list[this.state.activeIndex]) {
				e.preventDefault();
				this.onSelect(list[this.state.activeIndex]);
			}
		} else if (e.key === "Escape") {
			if (this.state.open) {
				e.preventDefault();
				this.close();
			}
		}
	};

	clear = e => {
		e.preventDefault();
		e.stopPropagation();
		this.props.onChange(null);
		this.setState({ query: "" });
	};

	renderMenu() {
		const list = this._filtered();
		const { activeIndex } = this.state;
		const rows = [];
		let lastGroup = null;
		list.forEach((o, i) => {
			const g = o.group || null;
			if (g && g !== lastGroup) {
				rows.push(
					<div key={"g" + i} className="filter-combobox__group">
						{g}
					</div>,
				);
			}
			lastGroup = g;
			rows.push(
				<div
					key={"o" + i}
					ref={el => {
						if (i === activeIndex) this._activeEl = el;
					}}
					className={"filter-combobox__option" + (i === activeIndex ? " filter-combobox__option--active" : "")}
					title={o.label}
					onMouseEnter={() => this.setState({ activeIndex: i })}
					onMouseDown={e => {
						e.preventDefault();
						this.onSelect(o);
					}}
				>
					{o.label}
				</div>,
			);
		});

		return ReactDOM.createPortal(
			<div
				className="filter-combobox__menu"
				style={this.state.menuStyle || { position: "fixed", visibility: "hidden" }}
				ref={el => (this._menuEl = el)}
			>
				{rows.length === 0 ? <div className="filter-combobox__empty">No matches</div> : rows}
			</div>,
			document.body,
		);
	}

	render() {
		const { placeholder, value, disabled } = this.props;
		const { open, query } = this.state;
		const inputValue = open ? query : this._selectedLabel();
		const hasValue = value != null && value !== "";

		return (
			<div className={"filter-combobox" + (disabled ? " filter-combobox--disabled" : "")} ref={this.wrapRef}>
				<input
					className="filter-combobox__input"
					type="text"
					value={inputValue}
					placeholder={placeholder}
					disabled={disabled}
					onFocus={this.open}
					onChange={this.onInputChange}
					onKeyDown={this.onKeyDown}
					autoComplete="off"
				/>
				{hasValue && !disabled && (
					<button type="button" className="filter-combobox__clear" onMouseDown={this.clear} tabIndex={-1} title="Clear">
						<Icon.X size={12} />
					</button>
				)}
				<Icon.ChevronDown size={13} className="filter-combobox__caret" />
				{open && this.renderMenu()}
			</div>
		);
	}
}

export default FilterCombobox;
